import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Store unificado y simplificado
export const useStore = create(
  persist(
    (set, get) => ({
      // Estado de autenticación
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      // Estado del carrito
      cart: [], // Array de productos { id, name, price, imageUrl, denomination, type, quantity, stock }

      // Estado de UI
      loading: false,
      mobileMenuOpen: false,

      // Métodos de autenticación
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setToken: (token) => set({ token }),
      
      login: (user, token) => {
        // Actualizar estado y localStorage de forma síncrona
        if (token) {
          localStorage.setItem('token', token);
        }
        
        // Forzar la actualización del estado
        set({ 
          user, 
          token, 
          isAuthenticated: true,
          isLoading: false
        });
        
        // Forzar la persistencia inmediata
        const state = get();
        localStorage.setItem('vinogenes-storage', JSON.stringify({
          state: {
            user: state.user,
            token: state.token,
            isAuthenticated: state.isAuthenticated,
            cart: state.cart,
          },
          version: 0
        }));
      },

      logout: () => {
        // Limpiar localStorage primero
        localStorage.removeItem('token');
        set({ 
          user: null, 
          token: null, 
          isAuthenticated: false,
          isLoading: false,
          cart: [] // También limpiar carrito en logout
        });
      },

      setLoading: (isLoading) => set({ isLoading }),

      isAdmin: () => {
        const { user } = get();
        return user?.role === 'admin';
      },

      // Métodos del carrito
      addToCart: (wine) => set((state) => {
        const categoryName =
          wine.categoryName || wine.denomination || wine.category?.name || '';
        const cartItem = { ...wine, categoryName, quantity: 1 };

        const existingItem = state.cart.find(item => item.id === wine.id);
        if (existingItem) {
          return {
            cart: state.cart.map(item =>
              item.id === wine.id
                ? {
                    ...item,
                    quantity: Math.min(item.quantity + 1, wine.stock),
                    categoryName: item.categoryName || categoryName
                  }
                : item
            )
          };
        }
        return {
          cart: [...state.cart, cartItem]
        };
      }),

      removeFromCart: (wineId) => set((state) => ({
        cart: state.cart.filter(item => item.id !== wineId)
      })),

      updateCartQuantity: (wineId, quantity) => set((state) => ({
        cart: state.cart.map(item =>
          item.id === wineId
            ? { ...item, quantity: Math.max(0, quantity) }
            : item
        ).filter(item => item.quantity > 0)
      })),

      clearCart: () => set({ cart: [] }),

      getCartTotal: () => {
        const { cart } = get();
        return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
      },

      getCartItemCount: () => {
        const { cart } = get();
        return cart.reduce((count, item) => count + item.quantity, 0);
      },

      // Métodos de UI
      setUILoading: (loading) => set({ loading }),
      
      toggleMobileMenu: () => set(state => ({ 
        mobileMenuOpen: !state.mobileMenuOpen 
      })),
      
      closeMobileMenu: () => set({ mobileMenuOpen: false }),
    }),
    {
      name: 'vinogenes-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        cart: state.cart,
      }),
    }
  )
);

// Mantener los stores originales para compatibilidad
export const useAuthStore = useStore;
export const useCartStore = useStore;
export const useUIStore = useStore;

// Filters Store
export const useFiltersStore = create((set, get) => ({
  filters: {
    search: '',
    denomination: '',
    type: '',
    minPrice: '',
    maxPrice: '',
    featured: false,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  },
  
  setFilter: (key, value) => {
    set(state => ({
      filters: {
        ...state.filters,
        [key]: value
      }
    }));
  },
  
  setFilters: (newFilters) => {
    set(state => ({
      filters: {
        ...state.filters,
        ...newFilters
      }
    }));
  },
  
  clearFilters: () => {
    set({
      filters: {
        search: '',
        denomination: '',
        type: '',
        minPrice: '',
        maxPrice: '',
        featured: false,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      }
    });
  },
  
  getQueryParams: () => {
    const { filters } = get();
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    return params.toString();
  }
})); 